import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMembership } from '../contexts/MembershipContext';
import { useAuth } from '../contexts/AuthContext';
import membershipService, {
  SubscriptionTier,
  SubscriptionStatus,
  PreviewUpdateResponse,
  DowngradeNextCycleResponse,
} from '../services/membershipService';
import DowngradeConfirmationModal from '../components/DowngradeConfirmationModal';

const SubscriptionPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { membershipInfo, loading, getTierName } = useMembership();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<number | null>(null);
  const [managingBilling, setManagingBilling] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewUpdateResponse | null>(
    null
  );
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTierLevel, setSelectedTierLevel] = useState<number | null>(
    null
  );
  const [updating, setUpdating] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedDowngradeTier, setSelectedDowngradeTier] =
    useState<SubscriptionTier | null>(null);
  const [downgradeResult, setDowngradeResult] =
    useState<DowngradeNextCycleResponse | null>(null);
  const [showDowngradeSuccess, setShowDowngradeSuccess] = useState(false);
  const [downgrading, setDowngrading] = useState(false);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
  }, [authLoading, user, username, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Fetch available tiers
        const tiersResponse = await membershipService.getAvailableTiers();
        setTiers(tiersResponse.tiers);
        // Fetch subscription status
        try {
          const statusResponse =
            await membershipService.getSubscriptionStatus();
          setSubscriptionStatus(statusResponse);
        } catch (error) {
          // User might not have a subscription yet
          setSubscriptionStatus(null);
        }
      } catch (error) {
        console.error('Failed to fetch subscription data:', error);
      } finally {
        setTiersLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Pricing mapping for display info
  const tierPricing: Record<
    string,
    { price: string; features: string[]; popular: boolean }
  > = {
    'Starter (Free)': {
      price: '$0',
      features: [
        '15 minutes of LIVE interview per month',
        'No additional minutes',
        'Random easy questions only',
      ],
      popular: false,
    },
    Practice: {
      price: '$12.99',
      features: [
        '60 minutes of LIVE interview per month',
        '$0.24 every additional minute',
        'Customizable questions',
        'Technical support',
      ],
      popular: false,
    },
    Pro: {
      price: '$29.99',
      features: [
        '150 minutes of LIVE interview per month',
        '$0.22 every additional minute',
        'Customizable questions',
        'Prioritized technical support',
      ],
      popular: false,
    },
    Onsite: {
      price: '$64.99',
      features: [
        '350 minutes of LIVE interview per month',
        '$0.20 every additional minute',
        'Customizable questions',
        'Prioritized technical support',
      ],
      popular: false,
    },
  };

  const handleBillingPortal = async () => {
    if (!user || managingBilling) return;

    setManagingBilling(true);
    try {
      const response = await membershipService.manageSubscription();
      window.location.href = response.portal_url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
      setManagingBilling(false);
    }
  };

  const handlePreviewUpgrade = async (tierLevel: number, tierName: string) => {
    if (!user || !membershipInfo) return;

    // For free tier, just redirect to practice
    if (tierName.includes('Free') || tierName.includes('Starter')) {
      navigate('/practice');
      return;
    }

    if (membershipInfo.tier === 1) {
      handleSubscribe(tierLevel, tierName);
      return;
    }

    // If it's the same tier or downgrade, handle differently
    if (membershipInfo.tier >= tierLevel) {
      return;
    }

    try {
      const preview = await membershipService.previewSubscriptionUpdate(
        tierLevel
      );
      setPreviewData(preview);
      setSelectedTierLevel(tierLevel);
      setShowPreview(true);
    } catch (error) {
      console.error('Failed to preview upgrade:', error);
      alert('Failed to preview upgrade cost. Please try again.');
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedTierLevel || !user || updating) return;

    setUpdating(true);
    try {
      await membershipService.updateSubscription(selectedTierLevel);

      // Success - redirect to success page
      setShowPreview(false);
      setPreviewData(null);
      setSelectedTierLevel(null);

      // Redirect to success page
      navigate('/subscription/success');
    } catch (error) {
      console.error('Failed to upgrade subscription:', error);
      alert('Failed to upgrade subscription. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setSelectedTierLevel(null);
  };

  const handleDowngradeClick = (tier: SubscriptionTier) => {
    setSelectedDowngradeTier(tier);
    setShowDowngradeModal(true);
  };

  const handleConfirmDowngrade = async (tierLevel: number) => {
    if (!selectedDowngradeTier) return;

    setDowngrading(true);
    try {
      const result = await membershipService.downgradeNextCycle(tierLevel);
      setDowngradeResult(result);
      setShowDowngradeModal(false);
      setShowDowngradeSuccess(true);

      // Refresh subscription status
      try {
        const statusResponse = await membershipService.getSubscriptionStatus();
        setSubscriptionStatus(statusResponse);
      } catch (error) {
      }
    } catch (error) {
      console.error('Failed to downgrade subscription:', error);
      alert('Failed to process downgrade. Please try again.');
    } finally {
      setDowngrading(false);
    }
  };

  const handleCloseDowngradeModal = () => {
    setShowDowngradeModal(false);
    setSelectedDowngradeTier(null);
  };

  const handleCloseDowngradeSuccess = () => {
    setShowDowngradeSuccess(false);
    setDowngradeResult(null);
  };

  const handleSubscribe = async (tierLevel: number, tierName: string) => {
    if (!user) return;


    // For free tier, just redirect to practice
    if (tierName.includes('Free') || tierName.includes('Starter')) {
      navigate('/practice');
      return;
    }

    setSubscribing(tierLevel);
    try {
      const currentOrigin = window.location.origin;
      const response = await membershipService.subscribe({
        tier_level: tierLevel,
        success_url: `${currentOrigin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${currentOrigin}/subscription/cancel`,
      });
      window.location.href = response.checkout_url;
    } catch (error) {
      console.error('Subscription failed:', error);
      alert('Failed to start subscription process. Please try again.');
      setSubscribing(null);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-gray-50'>
        <div className='text-gray-600'>Loading...</div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className='overflow-y-auto h-[100vh] bg-gradient-to-br from-gray-50 to-gray-100'>
      <div className='pt-16 pb-32 px-4'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <div className='text-center mb-8'>
            <h1 className='text-4xl font-bold text-gray-900 mb-4'>
              Manage Your Subscription
            </h1>
            <p className='text-xl text-gray-600 max-w-2xl mx-auto'>
              View your current usage and upgrade your plan to get more
              interview practice time.
            </p>
          </div>

          {/* Usage Overview */}
          <div className='max-w-2xl mx-auto mb-12'>
            <div className='bg-white rounded-2xl shadow-xl border border-gray-200 p-8'>
              <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                Usage Overview
              </h2>

              {loading ? (
                <div className='text-center text-gray-600'>
                  Loading usage data...
                </div>
              ) : membershipInfo ? (
                <div className='space-y-6'>
                  {/* Subscription Status - Hide for Starter (Free) tier */}
                  {subscriptionStatus && membershipInfo.tier !== 1 && (
                    <div className='space-y-4'>
                      <div className='text-sm text-gray-600'>
                        Subscription Status:{' '}
                        <span
                          className={`font-medium ${
                            subscriptionStatus.subscription_status === 'active'
                              ? 'text-green-600'
                              : 'text-yellow-600'
                          }`}
                        >
                          {subscriptionStatus.subscription_status}
                        </span>
                      </div>

                      {/* Cancellation Notice */}
                      {subscriptionStatus.cancel_at_period_end && (
                        <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
                          <div className='text-sm text-yellow-800'>
                            ⚠️ Your subscription will be cancelled on{' '}
                            {new Date(
                              subscriptionStatus.current_period_end * 1000
                            ).toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                              timeZone:
                                Intl.DateTimeFormat().resolvedOptions()
                                  .timeZone,
                            })}
                            {'. '} You will be charged the additional minutes
                            based on your current plan in the next billing
                            cycle.
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Current Plan */}
                  <div className='bg-blue-50 rounded-lg p-4'>
                    <div className='text-center'>
                      <div className='text-lg font-medium text-blue-900 mb-1'>
                        Current Plan: {getTierName(membershipInfo.tier)}
                      </div>
                      <div className='text-2xl font-bold text-blue-600'>
                        {membershipInfo.monthly_minutes} min/month
                      </div>
                    </div>
                  </div>

                  {/* Usage Bar */}
                  <div className='space-y-3'>
                    <div className='flex justify-between items-center'>
                      <span className='text-sm font-medium text-gray-700'>
                        Minutes used this month
                      </span>
                      <span className='text-sm text-gray-600'>
                        {membershipInfo.used_minutes} /{' '}
                        {membershipInfo.monthly_minutes} min
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className='w-full bg-gray-200 rounded-full h-4'>
                      <div
                        className='bg-blue-600 h-4 rounded-full transition-all duration-500'
                        style={{
                          width: `${Math.min(
                            (membershipInfo.used_minutes /
                              membershipInfo.monthly_minutes) *
                              100,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>

                    <div className='text-sm text-gray-600'>
                      {membershipInfo.remaining_minutes} minutes remaining •
                      Usage renews on{' '}
                      {(() => {
                        const endDate = new Date(
                          membershipInfo.billing_period.end_date
                            .replace('Z', '')
                            .replace(/[+-]\d{2}:\d{2}$/, '')
                        );
                        const renewalDate = new Date(
                          endDate.getTime() + 60 * 1000
                        ); // Add 1 minute
                        return renewalDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        });
                      })()}
                    </div>
                  </div>

                  {/* Subscription Management Buttons - Hide for Starter (Free) tier */}
                  {membershipInfo.tier !== 1 && (
                    <div className='pt-4 text-center space-y-3'>
                      <button
                        onClick={handleBillingPortal}
                        disabled={managingBilling}
                        className='bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-6 rounded-lg transition-colors mr-3 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        {managingBilling ? (
                          <div className='flex items-center justify-center'>
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                            Opening Portal...
                          </div>
                        ) : (
                          'Manage Billing'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className='text-center text-gray-500'>
                  Unable to load usage data
                </div>
              )}
            </div>
          </div>

          {/* Plan Options */}
          <div className='mb-8'>
            <h2 className='text-3xl font-bold text-center text-gray-900 mb-4'>
              Upgrade Your Plan
            </h2>
          </div>

          {/* Pricing Cards */}
          {tiersLoading ? (
            <div className='text-center py-8'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
              <p className='text-gray-600'>Loading plans...</p>
            </div>
          ) : (
            <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8'>
              {tiers.map((tier) => {
                const pricingInfo = tierPricing[tier.name] || null;

                const isCurrent = membershipInfo?.tier === tier.tier_level;
                const isDowngrade = Boolean(
                  membershipInfo && membershipInfo.tier > tier.tier_level
                );

                return pricingInfo == null ? null : (
                  <div
                    key={tier.id}
                    className={`relative bg-white rounded-2xl shadow-xl border-2 flex flex-col h-full ${
                      isCurrent
                        ? 'border-green-500 ring-4 ring-green-100'
                        : pricingInfo.popular && !isCurrent
                        ? 'border-blue-500 ring-4 ring-blue-100'
                        : 'border-gray-200'
                    } ${isDowngrade ? 'opacity-75' : ''}`}
                  >
                    {isCurrent && (
                      <div className='absolute -top-4 left-1/2 transform -translate-x-1/2'>
                        <span className='bg-green-500 text-white px-4 py-1 rounded-full text-sm font-medium'>
                          Current Plan
                        </span>
                      </div>
                    )}

                    {pricingInfo.popular && !isCurrent && (
                      <div className='absolute -top-4 left-1/2 transform -translate-x-1/2'>
                        <span className='bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium'>
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className='p-8 flex flex-col flex-grow'>
                      {/* Plan Name */}
                      <h3 className='text-2xl font-bold text-gray-900 mb-2'>
                        {tier.name}
                      </h3>

                      {/* Price */}
                      <div className='mb-6'>
                        <span className='text-4xl font-bold text-gray-900'>
                          {pricingInfo.price}
                        </span>
                        <span className='text-gray-600 ml-1'>/month</span>
                      </div>

                      {/* Minutes */}
                      <div className='mb-6 p-4 bg-blue-50 rounded-lg'>
                        <div className='text-center'>
                          <div className='text-2xl font-bold text-blue-600'>
                            {tier.monthly_minutes} min
                          </div>
                          <div className='text-sm text-blue-600'>per month</div>
                        </div>
                      </div>

                      {/* Features */}
                      <ul className='space-y-3 mb-8 flex-grow'>
                        {pricingInfo.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className='flex items-start'
                          >
                            <svg
                              className='w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M5 13l4 4L19 7'
                              />
                            </svg>
                            <span className='text-gray-700 text-sm'>
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA Button */}
                      <button
                        onClick={() => {
                          // Handle downgrade
                          if (isDowngrade) {
                            handleDowngradeClick(tier);
                          }
                          // If user has active subscription, show preview for upgrades
                          else if (
                            subscriptionStatus &&
                            !isCurrent &&
                            !isDowngrade
                          ) {
                            handlePreviewUpgrade(tier.tier_level, tier.name);
                          } else {
                            handleSubscribe(tier.tier_level, tier.name);
                          }
                        }}
                        disabled={isCurrent || subscribing === tier.id}
                        className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors mt-auto ${
                          isCurrent
                            ? 'bg-green-600 cursor-not-allowed'
                            : isDowngrade
                            ? 'bg-gray-600 hover:bg-gray-700'
                            : tier.name.includes('Free') ||
                              tier.name.includes('Starter')
                            ? 'bg-gray-600 hover:bg-gray-700'
                            : 'bg-blue-600 hover:bg-blue-700'
                        } ${
                          subscribing === tier.id
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        {subscribing === tier.id ? (
                          <div className='flex items-center justify-center'>
                            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                            Processing...
                          </div>
                        ) : isCurrent ? (
                          'Current Plan'
                        ) : isDowngrade ? (
                          'Downgrade'
                        ) : tier.name.includes('Free') ||
                          tier.name.includes('Starter') ? (
                          'Current Plan'
                        ) : (
                          `Upgrade to ${tier.name}`
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Back to Profile */}
          <div className='mt-12 text-center'>
            <button
              onClick={() => navigate('/profile/')}
              className='text-gray-600 hover:text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors'
            >
              ← Back to Profile
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-2xl shadow-xl max-w-md w-full p-6'>
            <div className='text-center mb-6'>
              <div className='w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center'>
                <svg
                  className='w-8 h-8 text-blue-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                  />
                </svg>
              </div>
              <h3 className='text-2xl font-bold text-gray-900 mb-2'>
                Upgrade Preview
              </h3>
              <p className='text-gray-600'>
                Upgrading from{' '}
                <span className='font-medium'>{previewData.current_tier}</span>{' '}
                to <span className='font-medium'>{previewData.new_tier}</span>
              </p>
            </div>

            <div className='space-y-4 mb-6'>
              {/* Minutes Upgrade */}
              <div className='bg-blue-50 rounded-lg p-4'>
                <div className='text-center'>
                  <div className='text-lg font-bold text-blue-600 mb-2'>
                    {previewData.current_tier_minutes} min →{' '}
                    {previewData.new_tier_minutes} min
                  </div>
                  <div className='text-sm text-blue-600'>
                    Monthly minutes upgrade
                  </div>
                </div>
              </div>

              {/* Proration Info */}
              <div className='bg-blue-50 rounded-lg p-4 border border-blue-200'>
                <div className='text-center'>
                  <div className='text-lg font-bold text-blue-600 mb-2'>
                    Next Bill Adjustment
                  </div>
                  <div className='text-sm text-blue-600'>
                    Your next bill will include a prorated charge for the
                    remaining time at the new tier rate
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className='space-y-2 text-sm text-gray-600'>
                {previewData.current_period_end && (
                  <div className='flex justify-between'>
                    <span>Current period ends:</span>
                    <span className='font-medium'>
                      {new Date(
                        previewData.current_period_end * 1000
                      ).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        timeZone:
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className='border-t pt-4 space-y-3'>
                <p className='text-sm text-gray-700'>{previewData.message}</p>
                <div className='bg-yellow-50 rounded-lg p-3 border border-yellow-200'>
                  <div className='flex items-start'>
                    <svg
                      className='w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                        clipRule='evenodd'
                      />
                    </svg>
                    <div className='text-sm text-yellow-700'>
                      <p className='font-medium mb-1'>Billing Details</p>
                      <p>
                        Your upgrade takes effect immediately, but the prorated
                        charge will appear on your next monthly bill. You'll
                        receive the full benefits of your new tier right away.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='flex space-x-3'>
              <button
                onClick={handleCancelPreview}
                className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors'
                disabled={updating}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpgrade}
                disabled={updating}
                className='flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {updating ? (
                  <div className='flex items-center justify-center'>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                    Upgrading...
                  </div>
                ) : (
                  'Confirm Upgrade'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Confirmation Modal */}
      {showDowngradeModal && selectedDowngradeTier && membershipInfo && (
        <DowngradeConfirmationModal
          isOpen={showDowngradeModal}
          onClose={handleCloseDowngradeModal}
          onConfirm={handleConfirmDowngrade}
          currentTier={{
            id: membershipInfo.tier,
            name: getTierName(membershipInfo.tier),
            tier_level: membershipInfo.tier,
            monthly_minutes: membershipInfo.monthly_minutes,
            stripe_price_id: '',
          }}
          targetTier={selectedDowngradeTier}
          currentPeriodEnd={subscriptionStatus?.current_period_end}
          isLoading={downgrading}
        />
      )}

      {/* Downgrade Success Modal */}
      {showDowngradeSuccess && downgradeResult && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full mx-4'>
            <div className='p-6'>
              <div className='flex items-center mb-4'>
                <div className='w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3'>
                  <svg
                    className='w-6 h-6 text-green-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                </div>
                <h3 className='text-lg font-semibold text-gray-900'>
                  {downgradeResult.action ===
                  'subscription_cancellation_scheduled'
                    ? 'Cancellation Scheduled'
                    : 'Downgrade Scheduled'}
                </h3>
              </div>

              <div className='mb-6'>
                <p className='text-gray-700 mb-4'>{downgradeResult.message}</p>

                <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
                  <div className='text-center'>
                    <div className='font-semibold text-green-900 mb-2'>
                      {downgradeResult.current_tier} →{' '}
                      {downgradeResult.target_tier}
                    </div>
                    <div className='text-sm text-green-700'>
                      Effective:{' '}
                      {new Date(
                        downgradeResult.effective_date * 1000
                      ).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-end'>
                <button
                  onClick={handleCloseDowngradeSuccess}
                  className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors'
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
